import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import {
  getMemberships,
  MEMBERSHIP_LENGTHS,
  type MembershipsSearchParams,
} from "../components/memberships/api";
import { MembershipsFilters } from "../components/memberships/MembershipsFilters";
import { MembershipsExpiryStrip } from "../components/memberships/MembershipsExpiryStrip";
import { MembershipsTable } from "../components/memberships/MembershipsTable";

const searchSchema = z.object({
  q: z.string().optional(),
  status: z.enum(["active", "inactive", "expired", "upcoming", "all"]).optional(),
  service_name: z.string().optional(),
  exclude_service_name: z.array(z.string()).optional(),
  category: z.string().optional(),
  exclude_category: z.array(z.string()).optional(),
  // Term length. Enumerated rather than a free string so a hand-edited URL
  // can't ask for a bucket outside the roster's scope (trainer, day_pass,
  // comp), which would return an empty list with no hint as to why.
  bucket: z.enum(MEMBERSHIP_LENGTHS).optional(),
  include_single_visit: z.boolean().optional(),
  // Signed: +N days ahead, -N days back. A min(0) here silently dropped a
  // negative out of the URL, so the filter appeared to do nothing at all.
  expiring_within_days: z.number().int().min(-3650).max(3650).optional(),
  expiry_from: z.string().optional(),
  expiry_to: z.string().optional(),
  show_all_terms: z.boolean().optional(),
  sort: z.enum(["expiry", "start", "client", "type"]).optional(),
  sort_dir: z.enum(["asc", "desc"]).optional(),
  page: z.number().int().min(1).default(1),
  page_size: z.number().int().min(1).max(200).default(50),
});

export const Route = createFileRoute("/_auth/memberships/")({
  staticData: { titleKey: "nav.memberships", breadcrumbKey: "breadcrumb.operate", breadcrumbN: 6 },
  validateSearch: searchSchema,
  component: MembershipsRoster,
});

function MembershipsRoster() {
  const search = Route.useSearch() as MembershipsSearchParams;

  const { data, isLoading, error } = useQuery({
    queryKey: ["memberships", search],
    queryFn: () => getMemberships(search),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <MembershipsFilters search={search} />
      <MembershipsExpiryStrip search={search} />
      <MembershipsTable
        items={data?.items}
        isLoading={isLoading}
        error={error}
        page={data?.page}
        pageSize={data?.page_size}
        total={data?.total}
      />
    </div>
  );
}
